import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { admin } from '../utils/api';
import { toast } from 'sonner';
import { Briefcase, Users, Building, Calendar, TrendingUp, LogOut, AlertTriangle } from 'lucide-react';
import AdminListModal from '../components/AdminListModal';
import AdminCandidateDetailModal from '../components/AdminCandidateDetailModal';
import AdminOnboardingFunnel from '../components/AdminOnboardingFunnel';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');

const USER_COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'user_type', label: 'Type' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
];
const COMPANY_COLUMNS = [
    { key: 'name', label: 'Contact Name' },
    { key: 'company_name', label: 'Company', render: (r) => r.company_name || '—' },
    { key: 'email', label: 'Email' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
];
const CANDIDATE_COLUMNS = [
    { key: 'name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'created_at', label: 'Created', render: (r) => fmtDate(r.created_at) },
];
const JOB_COLUMNS = [
    { key: 'title', label: 'Title' },
    { key: 'company_name', label: 'Company' },
    { key: 'sector', label: 'Sector' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Posted', render: (r) => fmtDate(r.created_at) },
];
const TRIAL_COLUMNS = [
    { key: 'candidate_name', label: 'Candidate' },
    { key: 'job_title', label: 'Job' },
    { key: 'company_name', label: 'Company' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Booked', render: (r) => fmtDate(r.created_at) },
];

const SECTOR_LABELS = {
    property: 'Property',
    tech_product: 'Tech & Product',
    sales_account: 'Sales & Account Management',
    creative_events: 'Creative & Events',
    hr_people: 'HR & People',
    marketing_growth: 'Marketing & Growth',
    operations_admin: 'Operations & Admin',
    finance_accounting: 'Finance & Accounting',
    legal_compliance: 'Legal & Compliance',
};

const AdminDashboard = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Clickable stat drill-downs — read-only. Data is cached per source
    // fetch (usersData / jobsData / trialBookingsData) and re-filtered
    // client-side for each stat's specific view, rather than issuing a
    // fresh request per stat.
    const [usersData, setUsersData] = useState(null);
    const [jobsData, setJobsData] = useState(null);
    const [trialBookingsData, setTrialBookingsData] = useState(null);
    const [listModal, setListModal] = useState(null); // { title, columns, rows, emptyLabel } | null
    const [listModalType, setListModalType] = useState(null);
    const [listModalOpen, setListModalOpen] = useState(false);
    const [listLoading, setListLoading] = useState(false);
    const [selectedCandidateId, setSelectedCandidateId] = useState(null);

    useEffect(() => {
        loadStats();
    }, []);

    const loadStats = async () => {
        try {
            const res = await admin.getStats();
            setStats(res.data);
        } catch (error) {
            toast.error('Failed to load stats');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
        toast.success('Logged out successfully');
    };

    // Fetches (with simple per-source caching) then fills the drill-down
    // modal for a given stat. Companies/candidates/users share one fetch;
    // trials/active_trials/completed_trials share another — filtered
    // client-side rather than issuing a fresh request per stat.
    const openList = async (type) => {
        setListModalOpen(true);
        setListModalType(type);
        setListLoading(true);
        try {
            let rows = [];
            let columns = USER_COLUMNS;
            let emptyLabel = 'No records found.';
            let title = '';

            if (type === 'users' || type === 'companies' || type === 'candidates') {
                let users = usersData;
                if (!users) {
                    const res = await admin.getUsers();
                    users = res.data.users;
                    setUsersData(users);
                }
                if (type === 'users') { title = `Total Users (${users.length})`; columns = USER_COLUMNS; rows = users; emptyLabel = 'No users found.'; }
                if (type === 'companies') { const c = users.filter(u => u.user_type === 'company'); title = `Companies (${c.length})`; columns = COMPANY_COLUMNS; rows = c; emptyLabel = 'No company accounts found.'; }
                if (type === 'candidates') { const c = users.filter(u => u.user_type === 'candidate'); title = `Candidates (${c.length})`; columns = CANDIDATE_COLUMNS; rows = c; emptyLabel = 'No candidate accounts found.'; }
            } else if (type === 'jobs') {
                let jobs = jobsData;
                if (!jobs) {
                    const res = await admin.getJobs();
                    jobs = res.data.jobs;
                    setJobsData(jobs);
                }
                title = `Total Jobs Posted (${jobs.length})`;
                columns = JOB_COLUMNS;
                rows = jobs;
                emptyLabel = 'No jobs have been posted yet.';
            } else if (type === 'trials' || type === 'active_trials' || type === 'completed_trials') {
                let bookings = trialBookingsData;
                if (!bookings) {
                    const res = await admin.getTrialBookings();
                    bookings = res.data.trial_bookings;
                    setTrialBookingsData(bookings);
                }
                columns = TRIAL_COLUMNS;
                if (type === 'trials') { title = `Total Trials (${bookings.length})`; rows = bookings; emptyLabel = 'No trials have been booked yet.'; }
                if (type === 'active_trials') { const a = bookings.filter(b => b.status === 'in_progress'); title = `Active Trials (${a.length})`; rows = a; emptyLabel = 'No trials are currently active.'; }
                if (type === 'completed_trials') { const c = bookings.filter(b => b.status === 'completed'); title = `Completed Trials (${c.length})`; rows = c; emptyLabel = 'No trials have been completed yet.'; }
            }

            setListModal({ title, columns, rows, emptyLabel });
        } catch (error) {
            toast.error('Failed to load records');
            setListModalOpen(false);
        } finally {
            setListLoading(false);
        }
    };

    const closeList = () => {
        setListModalOpen(false);
        setListModal(null);
        setListModalType(null);
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

    return (
        <div className="min-h-screen bg-background">
            <header className="border-b border-border/50 bg-white sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <img src="/sampler-logo.png" alt="Sampler" className="h-10 w-auto" />
                        <h1 className="text-2xl font-bold" style={{fontFamily: 'Unbounded, sans-serif'}}>Sampler Admin</h1>
                    </div>
                    <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
                        <LogOut className="h-5 w-5" />
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                <h1 className="text-4xl font-bold mb-8" style={{fontFamily: 'Unbounded, sans-serif'}} data-testid="admin-heading">Admin Dashboard</h1>

                {stats?.ai_health?.generate_description_failures_24h > 0 && (
                    <div className="rounded-2xl p-4 mb-8 bg-amber-50 border border-amber-300 flex items-start gap-3" data-testid="ai-health-alert">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                        <div>
                            <p className="font-semibold text-amber-900">
                                AI job-description generation has failed {stats.ai_health.generate_description_failures_24h} time{stats.ai_health.generate_description_failures_24h > 1 ? 's' : ''} in the last 24 hours
                            </p>
                            <p className="text-sm text-amber-700 mt-0.5">
                                Last failure: {stats.ai_health.last_failure_at ? new Date(stats.ai_health.last_failure_at).toLocaleString('en-GB') : 'unknown'} — {stats.ai_health.last_failure_error}
                            </p>
                        </div>
                    </div>
                )}

                {stats?.founding_assignment_failures?.count > 0 && (
                    <div className="rounded-2xl p-4 mb-8 bg-red-50 border border-red-300" data-testid="founding-assignment-failed-alert">
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-semibold text-red-900">
                                    {stats.founding_assignment_failures.count} candidate{stats.founding_assignment_failures.count > 1 ? 's' : ''} failed Founding Member number assignment
                                </p>
                                <p className="text-sm text-red-700 mt-0.5">
                                    Usually self-heals the next time the candidate loads their dashboard. If it persists here, it needs manual review.
                                </p>
                            </div>
                        </div>
                        <div className="divide-y divide-red-200/70">
                            {stats.founding_assignment_failures.users.map((u) => (
                                <div key={u.id} className="flex items-center justify-between py-2 text-sm" data-testid={`founding-assignment-failed-row-${u.id}`}>
                                    <div>
                                        <span className="font-medium text-slate-800">{u.name || 'Unnamed candidate'}</span>
                                        <span className="text-slate-500 ml-2">{u.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-400">
                                            {u.founding_assignment_failed_at ? new Date(u.founding_assignment_failed_at).toLocaleString('en-GB') : ''}
                                        </span>
                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700" data-testid={`founding-assignment-failed-badge-${u.id}`}>
                                            Assignment Failed
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {stats?.rate_limit_alerts?.top_offenders_last_7d?.length > 0 && (
                    <div className="rounded-2xl p-4 mb-8 bg-orange-50 border border-orange-300" data-testid="rate-limit-alert">
                        <div className="flex items-start gap-3 mb-3">
                            <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
                            <div>
                                <p className="font-semibold text-orange-900">
                                    {stats.rate_limit_alerts.total_hits_last_24h} rate-limit hit{stats.rate_limit_alerts.total_hits_last_24h !== 1 ? 's' : ''} in the last 24 hours
                                </p>
                                <p className="text-sm text-orange-700 mt-0.5">
                                    Top IPs by hit count over the last 7 days — repeated hits on /auth/login or /auth/register may indicate credential stuffing or brute-force attempts.
                                </p>
                            </div>
                        </div>
                        <div className="divide-y divide-orange-200/70">
                            {stats.rate_limit_alerts.top_offenders_last_7d.map((r) => (
                                <div key={r.ip} className="py-2 text-sm" data-testid={`rate-limit-row-${r.ip}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-slate-800">{r.ip}</span>
                                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                            {r.hit_count} hit{r.hit_count !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        Paths: {r.paths.join(', ')}
                                        {r.emails_attempted?.length > 0 && <> · Emails attempted: {r.emails_attempted.join(', ')}</>}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5">
                                        First: {new Date(r.first_seen).toLocaleString('en-GB')} · Last: {new Date(r.last_seen).toLocaleString('en-GB')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    <div
                        className="stat-card cursor-pointer"
                        data-testid="total-users-stat"
                        role="button"
                        tabIndex={0}
                        onClick={() => openList('users')}
                        onKeyDown={(e) => e.key === 'Enter' && openList('users')}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-600">Total Users</span>
                            <Users className="h-5 w-5 text-primary" />
                        </div>
                        <p className="text-3xl font-bold text-slate-900">{stats?.total_users || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">Click to view all</p>
                    </div>
                    <div
                        className="stat-card cursor-pointer"
                        data-testid="total-companies-stat"
                        role="button"
                        tabIndex={0}
                        onClick={() => openList('companies')}
                        onKeyDown={(e) => e.key === 'Enter' && openList('companies')}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-600">Companies</span>
                            <Building className="h-5 w-5 text-secondary" />
                        </div>
                        <p className="text-3xl font-bold text-slate-900">{stats?.total_companies || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">Click to view all</p>
                    </div>
                    <div
                        className="stat-card cursor-pointer"
                        data-testid="total-candidates-stat"
                        role="button"
                        tabIndex={0}
                        onClick={() => openList('candidates')}
                        onKeyDown={(e) => e.key === 'Enter' && openList('candidates')}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-600">Candidates</span>
                            <Users className="h-5 w-5" style={{color: '#10B981'}} />
                        </div>
                        <p className="text-3xl font-bold text-slate-900">{stats?.total_candidates || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">Click to view all</p>
                    </div>
                    <div
                        className="stat-card cursor-pointer"
                        data-testid="total-trials-stat"
                        role="button"
                        tabIndex={0}
                        onClick={() => openList('trials')}
                        onKeyDown={(e) => e.key === 'Enter' && openList('trials')}
                    >
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-slate-600">Total Trials</span>
                            <Calendar className="h-5 w-5" style={{color: '#8B5CF6'}} />
                        </div>
                        <p className="text-3xl font-bold text-slate-900">{stats?.total_trials || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">Click to view all</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-2xl p-6 border border-border/50">
                        <h2 className="text-xl font-bold mb-4 text-slate-900">Platform Overview</h2>
                        <div className="space-y-3">
                            <div
                                className="flex justify-between items-center cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1 -mx-2 transition-colors"
                                data-testid="platform-overview-total-jobs"
                                role="button"
                                tabIndex={0}
                                onClick={() => openList('jobs')}
                                onKeyDown={(e) => e.key === 'Enter' && openList('jobs')}
                            >
                                <span className="text-slate-600">Total Jobs Posted</span>
                                <span className="font-bold text-slate-900">{stats?.total_jobs || 0}</span>
                            </div>
                            <div
                                className="flex justify-between items-center cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1 -mx-2 transition-colors"
                                data-testid="platform-overview-active-trials"
                                role="button"
                                tabIndex={0}
                                onClick={() => openList('active_trials')}
                                onKeyDown={(e) => e.key === 'Enter' && openList('active_trials')}
                            >
                                <span className="text-slate-600">Active Trials</span>
                                <span className="font-bold text-slate-900">{stats?.active_trials || 0}</span>
                            </div>
                            <div
                                className="flex justify-between items-center cursor-pointer hover:bg-slate-50 rounded-lg px-2 py-1 -mx-2 transition-colors"
                                data-testid="platform-overview-completed-trials"
                                role="button"
                                tabIndex={0}
                                onClick={() => openList('completed_trials')}
                                onKeyDown={(e) => e.key === 'Enter' && openList('completed_trials')}
                            >
                                <span className="text-slate-600">Completed Trials</span>
                                <span className="font-bold text-slate-900">{stats?.completed_trials || 0}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl p-6 border border-border/50">
                        <h2 className="text-xl font-bold mb-4 text-slate-900">Trials by Sector</h2>
                        <div className="space-y-3">
                            {stats?.trials_by_sector && Object.entries(stats.trials_by_sector).map(([sector, count]) => (
                                <div key={sector} className="flex justify-between items-center" data-testid={`sector-stat-${sector}`}>
                                    <span className="text-slate-600">{SECTOR_LABELS[sector] || sector}</span>
                                    <span className="font-bold text-slate-900">{count}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <AdminOnboardingFunnel />
            </main>

            {listModalOpen && (
                <AdminListModal
                    title={listModal?.title || ''}
                    columns={listModal?.columns || []}
                    rows={listModal?.rows || []}
                    loading={listLoading}
                    emptyLabel={listModal?.emptyLabel || 'No records found.'}
                    onRowClick={listModalType === 'candidates' ? (row) => setSelectedCandidateId(row.id) : undefined}
                    onClose={closeList}
                />
            )}

            {selectedCandidateId && (
                <AdminCandidateDetailModal
                    candidateId={selectedCandidateId}
                    onClose={() => setSelectedCandidateId(null)}
                />
            )}
        </div>
    );
};

export default AdminDashboard;